import React, { useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'

import IconDefault from '@assets/images/icons/mimeTypes/default.svg'
import IconDoc from '@assets/images/icons/mimeTypes/doc.svg'
import IconDocx from '@assets/images/icons/mimeTypes/docx.svg'
import IconJpeg from '@assets/images/icons/mimeTypes/jpeg.svg'
import IconJpg from '@assets/images/icons/mimeTypes/jpg.svg'
import IconJson from '@assets/images/icons/mimeTypes/json.svg'
import IconMp3 from '@assets/images/icons/mimeTypes/mp3.svg'
import IconPdf from '@assets/images/icons/mimeTypes/pdf.svg'
import IconPpt from '@assets/images/icons/mimeTypes/ppt.svg'
import IconPptx from '@assets/images/icons/mimeTypes/pptx.svg'
import IconPng from '@assets/images/icons/mimeTypes/png.svg'
import IconXls from '@assets/images/icons/mimeTypes/xls.svg'
import IconXlsx from '@assets/images/icons/mimeTypes/xlsx.svg'
import IconXml from '@assets/images/icons/mimeTypes/xml.svg'
import IconZip from '@assets/images/icons/mimeTypes/zip.svg'

import SvgIcon from '@/modules/base/components/svg-icon/svg-icon'

import { mimeTypes } from '@/modules/base/util/constants'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import thumbnailService from '@/modules/file/thumbnailService'
import { splitFilename } from '@/modules/_helpers/splitFilename'
import { useAppSelector } from '@/redux/store'

const {
  MIMETYPE_JPG,
  MIMETYPE_JPEG,
  MIMETYPE_PNG,
  MIMETYPE_PDF,
  MIMETYPE_DOC,
  MIMETYPE_DOCX,
  MIMETYPE_XLS,
  MIMETYPE_XLSX,
  MIMETYPE_PPT,
  MIMETYPE_PPTX,
  MIMETYPE_CSV,
  MIMETYPE_X_ZIP_COMPRESSED,
  MIMETYPE_ZIP,
} = mimeTypes

const useStyles = makeStyles((theme) => ({
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  txt: {
    position: 'absolute',
    textTransform: 'uppercase',
    borderRadius: 1,
    background: theme.grey1,
    color: theme.white,
    padding: '1px 5px 0',
    marginTop: 17,
    fontSize: 11,
  },
  thumbnail: {
    transform: ({ rotate }) => rotate ? `rotateZ(${rotate}deg)` : '',
  },
}))

const PlaceHolder = (props) => {
  const classes = useStyles(props)
  const { mimetype, objectUrl, thumbnailUrl, minioId, size, name } = props

  const thumbnail = useAppSelector(state => state.document.thumbnails.thumbnails[minioId])
  const dmsThumbnailUrl = useAppSelector((state) => state.tenant.settings.dmsThumbnailUrl)

  useWebSocketEvent(EventTypes.FILE_Prefix, (msg) => {
    if (msg.type === EventTypes.FILE_THUMBNAIL_DONE_DMS && msg.data.minioId === minioId) {
      thumbnailService.fetch(minioId, size, true)
    }
  })

  useEffect(() => {
    if (minioId) {
      thumbnailService.fetch(minioId, size)
    }
  }, [minioId])

  const getSvgControl = (svg, txt) => (<div className={classes.placeholder}>
    <SvgIcon svg={svg} height="48px" width="48px" customClass={classes.placeholder} />
    {txt ? <span className={classes.txt}>{txt}</span> : null}
  </div>)

  const makeContent = (previewOnly = false) => {
    switch (mimetype) {
      case MIMETYPE_JPG:
        return previewOnly || !objectUrl ? getSvgControl(IconJpg) : <img src={objectUrl} loading="lazy" />
      case MIMETYPE_JPEG:
        return previewOnly || !objectUrl ? getSvgControl(IconJpeg) : <img src={objectUrl} loading="lazy" />
      case MIMETYPE_PNG:
        return previewOnly || !objectUrl ? getSvgControl(IconPng) : <img src={objectUrl} loading="lazy" />
      case MIMETYPE_PDF:
        return getSvgControl(IconPdf)
      case MIMETYPE_DOC:
        return getSvgControl(IconDoc)
      case MIMETYPE_DOCX:
        return getSvgControl(IconDocx)
      case MIMETYPE_XLS:
      case MIMETYPE_CSV:
        return getSvgControl(IconXls)
      case MIMETYPE_XLSX:
        return getSvgControl(IconXlsx)
      case MIMETYPE_PPT:
        return getSvgControl(IconPpt)
      case MIMETYPE_PPTX:
        return getSvgControl(IconPptx)
      case MIMETYPE_X_ZIP_COMPRESSED:
      case MIMETYPE_ZIP:
        return getSvgControl(IconZip)
      default:
        if (!name) {
          return getSvgControl(IconDefault)
        }
        switch (splitFilename(name).suffix) {
          case '.xls':
          case '.csv':
            return getSvgControl(IconXls)
          case 'xlsx':
            return getSvgControl(IconXlsx)
          case '.doc':
            return getSvgControl(IconDoc)
          case '.docx':
            return getSvgControl(IconDocx)
          case '.ppt':
            return getSvgControl(IconPpt)
          case '.pptx':
            return getSvgControl(IconPptx)
          case '.json':
            return getSvgControl(IconJson)
          case '.mp3':
            return getSvgControl(IconMp3)
          case '.xml':
            return getSvgControl(IconXml)
          case '.zip':
            return getSvgControl(IconZip)
        }
        return getSvgControl(IconDefault)
    }
  }

  if (dmsThumbnailUrl && minioId && thumbnail?.[size]?.state !== 'ready') {
    return makeContent(true)
  }

  if (thumbnail?.[size]?.url) {
    return <img className={classes.thumbnail} src={thumbnail[size].url} loading="lazy" />
  }

  if (thumbnailUrl?.length > 0) {
    return <img className={classes.thumbnail} src={thumbnailUrl} loading="lazy" />
  }

  return makeContent()
}

PlaceHolder.propTypes = {
  mimetype: PropTypes.string.isRequired,
  objectUrl: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  rotate: PropTypes.number,
  size: PropTypes.number,
  name: PropTypes.string,
  minioId: PropTypes.string,
}

PlaceHolder.defaultProps = {
  size: 200,
}

export default PlaceHolder
